import React from 'react';

import { Responsive, WidthProvider } from 'react-grid-layout';

import { Empty, Spin } from 'antd';
import PropTypes from 'prop-types';

import BoxContainer from 'components/BoxContainer';

import IntlMessages from 'util/IntlMessages';

import BarChartCard from '../../screens/EventsDashboard/components/BarChart';
import ChartCard from '../../screens/EventsDashboard/components/ChartCard';
import CountChart from '../../screens/EventsDashboard/components/CountChart';
import CounterCard from '../../screens/EventsDashboard/components/Counter/CounterCard';
import PieChartCard from '../../screens/EventsDashboard/components/PieChart';
import TableCard from '../../screens/EventsDashboard/components/TableCard';
import styles from './styles.module.less';
import classNames from 'classnames';

const ResponsiveGridLayout = WidthProvider(Responsive);
/**
 * DashboardGrid presents the grid layout for dashboard visualizations.
 * It dynamically renders ChartCard, TableCard, PieChartCard, and CountChart components using react-grid-layout,
 * allowing drag-and-drop and resizing unless in mobile view.
 * Props provide the current dashboard entities, UI state, and handlers for editing, deleting, and exporting.
 * The component displays a spinner during loading or an Empty state if no entities are present.
 */

/**
 * @component DashboardGrid
 * @description
 * DashboardGrid is a core presentational component used in the dashboard module for visualizing multiple data-driven widgets
 * (charts, tables, pie charts, count charts) in a configurable, responsive grid layout.
 *
 * - Leverages `react-grid-layout` (ResponsiveGridLayout) for user-driven drag-and-drop and resizable grid management,
 *   adapting to desktop and mobile views.
 * - Each grid cell renders one of: ChartCard, TableCard, PieChartCard, or CountChart, mapping the received entities.
 * - Interactivity (drag/resize) is disabled automatically on mobile for usability.
 * - Loading and empty data states are visually handled using Ant Design's <Spin> and <Empty> components.
 *
 * @props
 * @param {Array} charts - List of chart objects to display.
 * @param {Array} tables - List of table objects to display.
 * @param {Array} pieCharts - List of pie chart objects to display.
 * @param {Array} countCharts - List of count chart objects to display.
 * @param {Object} filters - Current active filters for visualizations.
 * @param {boolean} refreshChart - When toggled, triggers refresh for chart visualizations.
 * @param {Array|Object} eventTypes - Event type info for rendering contextual or dynamic data.
 * @param {boolean} isMobile - Device viewport flag for mobile-specific UI logic.
 * @param {boolean} loading - Global data loading flag for dashboard content fetches.
 * @param {boolean} formLoading - Flag for additional UI locking when forms submit.
 * @param {function} checkLayoutChange - Callback for reporting user-triggered grid position/size changes upstream.
 * @param {function} onEdit - External handler, invoked to signal "edit" intents for a dashboard entity.
 * @param {function} onRefreshCharts - Global refresh callback for chart-like widgets.
 * @param {object} tableElementsRef - Ref to expose programmatic table access/manipulation (scroll, focus, export, etc).
 *
 */

const DashboardGrid = ({
  charts,
  tables,
  pieCharts,
  countCharts,
  counters,
  barCharts,
  filters,
  refreshChart,
  eventTypes,
  isMobile = false,
  loading = false,
  formLoading = false,
  checkLayoutChange,
  onEdit = () => {},
  tableElementsRef = {},
}) => {
  const hasNoData =
    !charts?.length &&
    !tables?.length &&
    !pieCharts?.length &&
    !countCharts?.length &&
    !counters?.length &&
    !barCharts?.length;
  const isLoading = loading || formLoading;

  const gridItemStyle = { height: '100%', minHeight: 0 };

  return (
    <BoxContainer
      className={classNames(styles.gridContainer, hasNoData && styles.noData)}
      loading={loading}
    >
      <div data-dashboard-grid-scroll style={{ overflowY: 'auto', maxHeight: 'calc(100vh - 200px)' }}>
        <ResponsiveGridLayout
          isDraggable={!isMobile}
          isResizable={!isMobile}
          className="layout"
          breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
          cols={{ lg: 24, md: 20, sm: 12, xs: 8, xxs: 4 }}
          rowHeight={50}
          margin={[10, 10]}
          onLayoutChange={checkLayoutChange}
          draggableHandle=".drag-handle"
          draggableCancel=".cancelDrag"
        >
          {charts.length > 0 &&
            charts.map(chart => (
              /* Dev: DO NOT move the div with data-grid inside the maped component,
              it throws an error on running time about references.
            */
              <div
                key={chart.id}
                data-widget-id={chart.id}
                data-grid={{ ...chart.layout, minW: 2, minH: 2, maxH: 20 }}
                className="gx-d-flex"
                style={gridItemStyle}
              >
                <ChartCard filters={filters} refresh={refreshChart} chart={chart} onEdit={onEdit} />
              </div>
            ))}

          {tables.length > 0 &&
            tables.map(table => (
              <div
                key={table.id}
                data-widget-id={table.id}
                data-grid={
                  isMobile
                    ? {
                        x: 0,
                        y: 0,
                        w: 4,
                        h: tableElementsRef.current[table?.id] || 5,
                        minH: 3,
                        maxH: 20,
                      }
                    : { ...table.layout, minW: 2, minH: 3, maxH: 20 }
                }
                className="gx-d-flex"
                style={gridItemStyle}
              >
                <TableCard
                  table={table}
                  refresh={refreshChart}
                  filters={filters}
                  eventTypes={eventTypes}
                  onEdit={onEdit}
                  elementsRef={tableElementsRef}
                />
              </div>
            ))}

          {pieCharts.length > 0 &&
            pieCharts.map(chart => (
              <div
                key={chart.id}
                data-widget-id={chart.id}
                data-grid={{ ...chart.layout, minW: 2, minH: 2, maxH: 20 }}
                className="gx-d-flex"
                style={gridItemStyle}
              >
                <PieChartCard
                  filters={filters}
                  refresh={refreshChart}
                  chart={chart}
                  onEdit={onEdit}
                />
              </div>
            ))}

          {countCharts.length > 0 &&
            countCharts.map(chart => (
              <div
                key={chart.id}
                data-widget-id={chart.id}
                data-grid={{ ...chart.layout, minW: 2, minH: 2, maxH: 20 }}
                className="gx-d-flex"
                style={gridItemStyle}
              >
                <CountChart
                  filters={filters}
                  refresh={refreshChart}
                  chart={chart}
                  onEdit={onEdit}
                />
              </div>
            ))}

          {counters.length > 0 &&
            counters.map(counter => (
              <div
                key={counter.id}
                data-widget-id={counter.id}
                data-grid={{ ...counter.layout, minW: 2, minH: 2, maxH: 20 }}
                className="gx-d-flex"
                style={gridItemStyle}
              >
                <CounterCard
                  filters={filters}
                  refresh={refreshChart}
                  chart={counter}
                  onEdit={onEdit}
                />
              </div>
            ))}

          {barCharts.length > 0 &&
            barCharts.map(barChart => (
              <div
                key={barChart.id}
                data-widget-id={barChart.id}
                data-grid={{ ...barChart.layout, minW: 2, minH: 2, maxH: 20 }}
                className="gx-d-flex"
                style={gridItemStyle}
              >
                <BarChartCard
                  filters={filters}
                  refresh={refreshChart}
                  chart={barChart}
                  onEdit={onEdit}
                />
              </div>
            ))}
        </ResponsiveGridLayout>
      </div>
      {isLoading && <Spin data-testid="dashboard-spin" />}
      {hasNoData && <Empty data-testid="dashboard-empty" description={<IntlMessages id="components.empty.description" />} />}
    </BoxContainer>
  );
};

DashboardGrid.propTypes = {
  charts: PropTypes.array.isRequired,
  tables: PropTypes.array.isRequired,
  pieCharts: PropTypes.array.isRequired,
  countCharts: PropTypes.array.isRequired,
  filters: PropTypes.object.isRequired,
  refreshChart: PropTypes.bool.isRequired,
  eventTypes: PropTypes.array,
  isMobile: PropTypes.bool,
  loading: PropTypes.bool.isRequired,
  formLoading: PropTypes.bool.isRequired,
  checkLayoutChange: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  tableElementsRef: PropTypes.object.isRequired,
};

export default DashboardGrid;
